/**
 * 125 Data & Insights Constants
 *
 * You can use ES6 destructuring to help Tree Shaking embed only the constants that are needed:
 *
 * import { FIB } from 'constants' // Import all of `FIB`
 * const { US } = FIB // Use destructuring to keep only what is needed
 */
export var FIB = {
  /*****************************************************************************
   * Guest App constants
   ****************************************************************************/
  MAX_PASSCODE_LENGTH: 5,
  MIN_PASSCODE_LENGTH: 3,
  POLLING_FREQUENCY: 5 * 60 * 1000,
  // 5 minutes in ms
  POST_NAVIGATION_DELAY: 50,

  /*****************************************************************************
   * localStorage keys
   ****************************************************************************/
  LOCAL_STORAGE: {
    COOKIE_CONSENT: 'guest-app-cookie-consent'
  },

  /*****************************************************************************
   * Update State
   *
   * States for the uploading/downloading of config, survey and response data.
   ****************************************************************************/
  US: {
    UNKNOWN: 0,
    DONE: 1,
    PENDING: 2,
    IN_PROGRESS: 3
  },

  /*****************************************************************************
   * Checkout Mode
   *
   * Form modes for the checkout flow.
   ****************************************************************************/
  CHECKOUT_MODE: {
    UNKNOWN: 0,
    JUST_CONTACT: 1,
    COMPLAINED: 2,
    INCENTIVE: 3,
    NO_INCENTIVE: 4
  },

  /*****************************************************************************
   * API Errors
   *
   * API error codes.
   ****************************************************************************/
  API_ERROR: {
    UNKNOWN: 10000,
    INTERNAL: 10001,
    UNKNOWN_ROUTE: 10002,
    LANGUAGE_REQUIRED: 10003,
    UNSUPPORTED_LANGUAGE: 10004,
    NOT_AUTHENTICATED: 10005,
    INVALID_REQUEST_FORMAT: 10006,
    INVALID_RESPONSE_FORMAT: 10007,
    PARAMETER_MISSING: 10008,
    PARAMETER_VALUE_INVALID: 10009,
    INVALID_EDGE: 10010,
    INVALID_COMPANY_REFERENCE: 10011,
    INVALID_BRANCH_REFERENCE: 10012,
    INVALID_TEMPLATE_REFERENCE: 10013,
    RESOURCE_NOT_FOUND: 10014,
    NOT_IMPLEMENTED: 10016,
    DEVICE_ALREADY_USED: 10017,
    DEVICE_REGISTRATION_NOT_AUTHENTICATED: 10018,
    CLIENT_REQUIRED: 10019,
    CLIENT_VERSION_NOT_SUPPORTED: 10020,
    SERVICE_UNAVAILABLE: 10021,
    CLIENT_INVALID: 10022,
    INVALID_EDGE_REQUIREMENTS: 10023,
    TRANSACTION_NO_EPOS: 10024,
    FORM_VALIDATION: 10025,
    MALFORMED_REQUEST: 10026,
    UPLOAD: 10027,
    INVALID_IP_ADDRESS: 10028,
    INVALID_RESPONSE_REFERENCE: 10029,
    INVALID_DEVICE_REFERENCE: 10030,
    TRANSACTION_PIN_ALREADY_USED: 10031,
    INVALID_PIN: 10032,
    INVALID_EDGE_COUNT: 10033
  },
  //////////////////////////////////////////////////////////////////////////////////////////////////
  //    ___           __        _______           _____              __            __
  //   / _ )___ _____/ /_____  / _/ _(_)______   / ___/__  ___  ___ / /____ ____  / /____
  //  / _  / _ `/ __/  '_/ _ \/ _/ _/ / __/ -_) / /__/ _ \/ _ \(_-</ __/ _ `/ _ \/ __(_-<
  // /____/\_,_/\__/_/\_\\___/_//_//_/\__/\__/  \___/\___/_//_/___/\__/\_,_/_//_/\__/___/

  /*****************************************************************************
   * Survey Modes
   *
   * These modes are used with defining the survey response rendering.
   ****************************************************************************/
  SURVEY_MODE: {
    SURVEY_MODE_NORMAL: 1,
    // SURVEY_MODE_PAPER: 2,
    SURVEY_MODE_STAFF: 3,
    SURVEY_MODE_TABLET: 4,
    SURVEY_MODE_QUICK_SURVEY: 5,
    // SURVEY_MODE_UNSTRUCTURED_FEEDBACK: 6,
    SURVEY_MODE_AUDIT: 7 // SURVEY_MODE_SOCIAL_REVIEW: 8,

  },

  /*****************************************************************************
   * Question Types
   ****************************************************************************/
  QUESTION_TYPE: {
    QUESTION_TYPE_DRILLDOWN: 1,
    QUESTION_TYPE_CONTEXT: 2,
    QUESTION_TYPE_PRODUCT: 3,
    QUESTION_TYPE_OTHER: 4,
    QUESTION_TYPE_MENU: 5,
    QUESTION_TYPE_DRILLDOWN_PLUS: 6,
    QUESTION_TYPE_PRODUCT_SCORECARD: 7,
    QUESTION_TYPE_MENU_INSIGHT: 8
  },

  /*****************************************************************************
   * Question Formats
   ****************************************************************************/
  QUESTION_FORMAT: {
    QUESTION_FORMAT_ONE_DROPDOWN: 1,
    QUESTION_FORMAT_ONE_RADIO: 2,
    QUESTION_FORMAT_ONE_CHECKBOX: 3,
    QUESTION_FORMAT_YESNOWHY: 4,
    QUESTION_FORMAT_TEXT_SINGLE: 5,
    QUESTION_FORMAT_TEXT_MULTIPLE: 6
  },

  /*****************************************************************************
   * Audit Actions
   ****************************************************************************/
  AUDIT_ACTION: {
    AUDIT_ACTION_CONFIRM_FLAGGED: 1,
    AUDIT_ACTION_UNFLAG: 2,
    AUDIT_ACTION_FLAG: 3,
    AUDIT_ACTION_COMPLETE: 4,
    AUDIT_ACTION_OPEN: 5,
    AUDIT_ACTION_DELETE: 6,
    AUDIT_ACTION_COMMENT: 7
  },

  /*****************************************************************************
   * Topic Types
   ****************************************************************************/
  TOPIC_TYPE: {
    TOPIC_TYPE_COMPLIMENT: 1,
    TOPIC_TYPE_COMPLAINT: 2,
    TOPIC_TYPE_QUERY: 3
  },

  /*****************************************************************************
   * Voucher Sub-Purposes
   ****************************************************************************/
  VOUCHER_SUBPURPOSE: {
    VOUCHER_SUBPURPOSE_INCENTIVE: 1,
    VOUCHER_SUBPURPOSE_INSTANT_WIN: 2
  },

  /*****************************************************************************
   * Voucher Code
   ****************************************************************************/
  VOUCHER_CODE: {
    VOUCHER_CODE_UNUSED: 1,
    VOUCHER_CODE_USED: 2,
    VOUCHER_CODE_DELETED: 4
  },

  /*****************************************************************************
   * Demographics
   ****************************************************************************/
  DEMOGRAPHIC: {
    DEMOGRAPHIC_GENDER: 1,
    DEMOGRAPHIC_AGE: 2,
    DEMOGRAPHIC_VISIT_PURPOSE: 3,
    DEMOGRAPHIC_VISIT_FREQUENCY: 4,
    DEMOGRAPHIC_POSTCODE: 5
  },

  /*****************************************************************************
   * Social Types
   ****************************************************************************/
  SOCIAL_TYPE: {
    SOCIAL_TYPE_TRIPADVISOR: 1,
    SOCIAL_TYPE_FACEBOOK: 2,
    SOCIAL_TYPE_GOOGLE: 3,
    SOCIAL_TYPE_TWITTER: 4,
    SOCIAL_TYPE_INSTAGRAM: 5,
    SOCIAL_TYPE_HOSTELWORLD: 6,
    SOCIAL_TYPE_PILLOWTALK: 7,
    SOCIAL_TYPE_AIRBNB: 8,
    SOCIAL_TYPE_BOOKINGCOM: 9
  },

  /*****************************************************************************
   * Survey Types
   ****************************************************************************/
  SURVEY_TYPE: {
    SURVEY_TYPE_FOOD_DRINK: 1,
    SURVEY_TYPE_ACCOMMODATION: 2,
    SURVEY_TYPE_FUNCTIONS: 3,
    SURVEY_TYPE_AUDIT: 4,
    SURVEY_TYPE_DELIVERY: 6,
    SURVEY_TYPE_TAKEAWAY: 7,
    SURVEY_TYPE_COFFEE: 8
  },

  /*****************************************************************************
   * PVE Sources
   ****************************************************************************/
  PVE_SOURCE: {
    PVE_SOURCE_WIRELESS_SOCIAL: 1,
    PVE_SOURCE_ZONAL_LOYALTY: 2,
    PVE_SOURCE_LIVERES: 3
  },

  /*****************************************************************************
   * EPOS Product Types
   ****************************************************************************/
  EPOS_PRODUCT_TYPE: {
    EPOS_PRODUCT_TYPE_FOOD: 1,
    EPOS_PRODUCT_TYPE_DRINK: 2,
    EPOS_PRODUCT_TYPE_OTHER: 3
  },

  /*****************************************************************************
   * Menu Insight Modes
   ****************************************************************************/
  MENU_INSIGHT_MODE: {
    TYPE: 1,
    FAMILY_GROUP: 2,
    MAJOR_GROUP: 3
  },

  /*****************************************************************************
   * Ask question based on the presence of a transaction
   ****************************************************************************/
  TX_ASK_QUESTION: {
    TX_ASK_QUESTION_ALWAYS: 0,
    TX_ASK_QUESTION_WHEN_TRANSACTION: 1,
    TX_ASK_QUESTION_WHEN_NO_TRANSACTION: 2
  },

  /*****************************************************************************
   * Track Actions
   ****************************************************************************/
  TRACK_ACTIONS: {
    SHARED_EMAIL: 'shared_email',
    SHARED_TWITTER: 'shared_twitter',
    SHARED_TRIPADVISOR: 'shared_tripadvisor',
    SHARED_FACEBOOK: 'shared_facebook',
    SHARED_UPLOADED_IMAGE: 'shared_uploaded_image'
  },

  /*****************************************************************************
   * Indicators Render Type
   ****************************************************************************/
  INDICATORS_RENDER_TYPE: {
    EMOJI: 0,
    RATING: 1,
    CUSTOM_RATING: 2,
    CUSTOM_EMOJI: 3
  },

  /*****************************************************************************
   * Staff Render Type
   ****************************************************************************/
  STAFF_RENDER_TYPE: {
    TEXT: 0,
    SELECT: 1
  },

  /*****************************************************************************
   * Staff Followup Type
   ****************************************************************************/
  STAFF_FOLLOWUP_TYPE: {
    NONE: 0,
    TAGS: 1,
    TEXT: 2,
    BOTH: 3
  },

  /*****************************************************************************
   * Indicator Kinds
   ****************************************************************************/
  INDICATOR_KIND: {
    SURVEY_RATING: 1,
    // e.g. Food, Drink etc for Surveys only.
    NPS: 2,
    CSAT: 3,
    REVIEW_RATING: 4 // e.g. Service, Atmosphere for Reviews only.

  }
};